import { FC, useMemo } from 'react';
import { isRouteErrorResponse, useRouteError } from '@remix-run/react';
import {
  SlardarScript,
  TeaScript,
  TeaDeferredScript,
  SlardarDeferredScript,
  FacebookDeferredScript,
  WebVitalMonitor,
  USBanBanner,
  USBanModal,
  PumbaaScript,
} from '@lemon8/web-app-shared/component';
import { BodyOutlet, HeadOutlet, I18nProvider, JotaiStoreInjector, Links, Scripts } from '@lemon8/web-app-shared/ssr';
import { Preconnect } from './preconnect';
import { RootHelmet } from './root-helmet';
import { createStore, Provider, useAtomValue } from 'jotai';
import { HydrationAtomFamiliesMap, HydrationAtomsMap } from 'shared/atoms';
import { $RootTraffic } from '@lemon8/web-app-shared/atom';
import { PageTypeUtils } from '~/utils/page-type-utils';
import { PageShowReporter } from '~/components/page-show';

import './root.scss';

const RootHtml: FC = () => {
  const { enterPageName } = useAtomValue($RootTraffic);
  return (
    <html>
      <head>
        <RootHelmet />
        <Preconnect />
        <TeaScript />
        <SlardarScript
          pageview={{
            extractPid: (url: string) => {
              const u = new URL(url);
              const pageType = PageTypeUtils.getPageTypeFromPathname(u.pathname);

              return pageType || 'legal-unknown';
            },
          }}
        />
        <Links />
        <HeadOutlet />
        <PumbaaScript />
        <TeaDeferredScript />
        <SlardarDeferredScript />
        <FacebookDeferredScript />
      </head>
      <body>
        <main>
          <BodyOutlet />
        </main>
        <Scripts />
      </body>
    </html>
  );
};

export const Root: FC = () => {
  if (__BUILD_TYPE__ !== 'online') {
    console.log('root rendered');
  }

  const store = useMemo(() => {
    if (__BUILD_TARGET__ === 'client') {
      return window.store;
    } else {
      return createStore();
    }
  }, []);

  return (
    <Provider store={store}>
      <JotaiStoreInjector atoms={HydrationAtomsMap} atomFamilies={HydrationAtomFamiliesMap as any} />
      <WebVitalMonitor />
      <PageShowReporter />
      <I18nProvider>
        <RootHtml />
      </I18nProvider>
    </Provider>
  );
};

export const RootErrorBoundary: FC = () => {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return (
      <html>
        <body>
          <div>
            <h1>
              {error.status} {error.statusText}
            </h1>
            <p>{error.data}</p>
          </div>
        </body>
      </html>
    );
  } else if (error instanceof Error) {
    if (__BUILD_TARGET__ === 'client') {
      window.Slardar('captureException', error);
    }

    return (
      <html>
        <body>
          <div>
            <h1>Error</h1>
            <p>{error.message}</p>
            <p>The stack trace is:</p>
            <pre>{error.stack}</pre>
          </div>
        </body>
      </html>
    );
  } else {
    return <h1>Unknown Error</h1>;
  }
};
